import { AnimationGroup, Mesh, TransformNode, Vector3 } from '@babylonjs/core';

import { Level } from './Level';

import { EnemyBehavior } from './behavior/EnemyBehavior';
import { EnemyDistributionConfig } from './EnemyGenerator';
import { Entity } from './Entity';
import { PlayerCollision } from './PlayerCollision';

/**
 * Represents an enemy instance in the game
 */
export class Enemy implements Entity {
  constructor(
    public id: string,
    public mesh: Mesh,
    public behavior: EnemyBehavior,
    public config: EnemyDistributionConfig,
    public spawnPoint: Vector3,
    private level: Level,
    public pathIndex: number,
    public rootNode?: TransformNode,
    public animations?: AnimationGroup[]
  ) {
    // Set up player collision to kill player on contact
    this.mesh.metadata = {
      ...this.mesh.metadata,
      playerCollision: new PlayerCollision(
        false, // disableCollision
        (level, player, event) => {
          if (player.dashing || player.isKicking || !player.vulnerable) {
            this.dispose();
          } else {
            player.collide(event, 'Killed by enemy!');
          }
        }
      ),
    };

    // Initialize behavior
    this.behavior.attach(this.mesh);
    this.behavior.initLevel(this.level);
  }

  /**
   * Update enemy every frame
   */
  update(deltaTime: number): void {
    if (!this.mesh || this.mesh.isDisposed()) {
      return;
    }
    this.behavior.update(deltaTime);
  }

  /**
   * Dispose of the enemy
   */
  dispose(): void {
    // Remove main mesh and all child meshes from collision meshes
    // AND unregister them from the player's ActionManager to prevent ghost collisions
    const meshesToRemove = [this.mesh, ...this.mesh.getChildMeshes(false, (n) => n instanceof Mesh)];
    for (const meshToRemove of meshesToRemove) {
      // Unregister from player's ActionManager FIRST (before mesh is disposed)
      if (this.level.player) {
        this.level.player.unregisterCollisionMesh(meshToRemove as Mesh);
      }

      const index = this.level.collisionMeshes.indexOf(meshToRemove as Mesh);
      if (index > -1) {
        this.level.collisionMeshes.splice(index, 1);
      }
    }

    // Detach behavior (also disposes cloned animations)
    if (this.behavior) {
      this.behavior.detach();
    }

    // Dispose animations
    if (this.animations) {
      for (const anim of this.animations) {
        anim.dispose();
      }
    }

    // Dispose root node and all children, but NOT materials (they are shared)
    if (this.rootNode && !this.rootNode.isDisposed()) {
      // dispose(doNotRecurse, disposeMaterialAndTextures)
      // false = recurse to children, false = don't dispose materials (shared with source)
      this.rootNode.dispose(false, false);
    } else if (this.mesh && !this.mesh.isDisposed()) {
      this.mesh.dispose(false, false);
    }
  }
}
